import mapboxgl, { Map } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useEffect, useRef } from 'react'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
mapboxgl.accessToken = MAPBOX_TOKEN

interface BaseMapProps {
  initialView: {
    center: [number, number]
    zoom: number
    pitch?: number
    bearing?: number
  }
  style: string

  onMapReady: (map: Map | null) => void
  onMapMove?: (map: Map) => void
  interactive?: boolean
  padding?: { top: number; right: number; bottom: number; left: number }
  maxPitch?: number
  children?: React.ReactNode
}

export const BaseMap = ({
  initialView,
  style,
  onMapReady,
  onMapMove,
  interactive = true,
  padding,
  maxPitch = 85,
  children,
}: BaseMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const mapRef = useRef<Map | null>(null)
  const onMapReadyRef = useRef(onMapReady)
  const onMapMoveRef = useRef(onMapMove)
  const styleRef = useRef(style)

  // Store initial view in ref to prevent re-creation
  const initialViewRef = useRef(initialView)

  useEffect(() => {
    onMapReadyRef.current = onMapReady
  }, [onMapReady])

  useEffect(() => {
    onMapMoveRef.current = onMapMove
  }, [onMapMove])

  // Map creation effect - runs ONCE
  useEffect(() => {
    if (!mapContainer.current) return
    if (mapRef.current) return

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: styleRef.current,
      center: initialViewRef.current.center,
      zoom: initialViewRef.current.zoom,
      pitch: initialViewRef.current.pitch ?? 0,
      bearing: initialViewRef.current.bearing ?? 0,
      maxPitch,
      interactive,
      attributionControl: false,
    })

    if (padding) map.setPadding(padding)

    map.addControl(new mapboxgl.AttributionControl(), 'bottom-left')
    map.addControl(new mapboxgl.ScaleControl({ unit: 'metric', maxWidth: 200 }))

    if (interactive) {
      map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }))
    }

    map.on('load', () => {
      mapRef.current = map
      onMapReadyRef.current(map)
    })

    map.on('moveend', () => {
      onMapMoveRef.current?.(map)
    })

    return () => {
      map.remove()
      mapRef.current = null
      onMapReadyRef.current(null)
    }
    // Intentionally empty — map is created once. Style/padding updates handled by separate effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!mapRef.current) return
    mapRef.current.setStyle(style)
  }, [style])

  // Separate effect for padding updates (no map recreation)
  useEffect(() => {
    if (!mapRef.current || !padding) return
    mapRef.current.setPadding(padding)
  }, [padding])

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      {children}
    </div>
  )
}
